import { useCallback, useContext } from 'react';
import AuthContext from '../store/auth-context';
import useHttp from './use-http';

const usePostContent = () => {
  const { isLoading, error, sendRequest } = useHttp();
  const authCtx = useContext(AuthContext);

  const postSingleText = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/pagetext',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postMultiTextEntry = useCallback(
    async (textId, newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/multipagetext/' + textId + '/text',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postNews = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/news',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postNewsThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/news/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postInternalNews = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/internalnews',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postShortcut = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/shortcut',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postShortcutThumbnail = useCallback(
    async (newData, shortcutId, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/shortcut/' + shortcutId + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postEvent = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/event',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postNewCarModel = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/newcar',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postNewCarThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/newcar/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postEmployee = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/person',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postEmployeeThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/person/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postEmployeeImage = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/person/' + _id + '/fullimage',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const addEmployeeToDepartment = useCallback(
    async (employeeId, departmentId, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/department/' + departmentId + '/member/' + employeeId,
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postDepartment = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/department',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postJob = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/job',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postJobThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/job/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postJobPdf = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/job/' + _id + '/pdf',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postProduct = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/product',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postProductThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/product/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postProductPdf = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/product/' + _id + '/pdf',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postCaravanClass = useCallback(
    async (newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/caravan',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postCaravanClassThumbnail = useCallback(
    async (newData, _id, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/caravan/' + _id + '/thumbnail',
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: newData,
            noJSON: true,
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postException = useCallback(
    async (_id, newData, applyData) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/openinghour/' + _id + '/exception',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postSuggestedCar = useCallback(
    async (data, applyData) => {
      const { _id, brand } = data;
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/suggestedcar/',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              referenceId: _id,
              brand,
            },
          },
          (data) => {
            applyData(data);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postUser = useCallback(
    async (newData, applyData, catchRequest) => {
      if (authCtx.isLoggedIn) {
        sendRequest(
          {
            url: '/api/v1/user',
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + authCtx.token,
            },
            body: {
              ...newData,
            },
          },
          (data) => {
            applyData(data);
          },
          (response) => {
            catchRequest(response);
          }
        );
      }
    },
    [authCtx.isLoggedIn, authCtx.token, sendRequest]
  );

  const postRequest = useCallback(
    async (newData, applyData) => {
      sendRequest(
        {
          url: '/api/v1/request',
          method: 'POST',
          body: newData,
          noJSON: true,
        },
        (data) => {
          applyData(data);
        }
      );
    },
    [sendRequest]
  );

  return {
    isLoading,
    error,
    postSingleText,
    postMultiTextEntry,
    postNews,
    postNewsThumbnail,
    postInternalNews,
    postShortcut,
    postShortcutThumbnail,
    postEvent,
    postNewCarModel,
    postNewCarThumbnail,
    postEmployee,
    postEmployeeThumbnail,
    postEmployeeImage,
    addEmployeeToDepartment,
    postDepartment,
    postJob,
    postJobThumbnail,
    postJobPdf,
    postProduct,
    postProductThumbnail,
    postProductPdf,
    postCaravanClass,
    postCaravanClassThumbnail,
    postException,
    postSuggestedCar,
    postUser,
    postRequest,
  };
};

export default usePostContent;
